import { useEffect, useRef, useState, useCallback } from 'react';

export type DropdownType = 'account' | 'locale-picker' | 'subnav' | null;

interface UseDropdownManagerReturn {
  activeDropdown: DropdownType;
  isDropdownOpen: (type: DropdownType) => boolean;
  openDropdown: (type: DropdownType) => void;
  closeDropdown: () => void;
  toggleDropdown: (type: DropdownType) => void;
  registerDropdownRef: (type: DropdownType, ref: HTMLElement | null) => void;
}

/**
 * Custom hook to manage dropdown state across multiple dropdown components.
 * Ensures only one dropdown is open at a time and handles outside click detection.
 */
export const useDropdownManager = (): UseDropdownManagerReturn => {
  const [activeDropdown, setActiveDropdown] = useState<DropdownType>(null);
  const dropdownRefs = useRef<Map<DropdownType, HTMLElement>>(new Map());

  const isDropdownOpen = useCallback((type: DropdownType): boolean => activeDropdown === type, [activeDropdown]);

  const openDropdown = useCallback((type: DropdownType) => {
    setActiveDropdown(type);
  }, []);

  const closeDropdown = useCallback(() => {
    setActiveDropdown(null);
  }, []);

  const toggleDropdown = useCallback((type: DropdownType) => {
    setActiveDropdown((current) => (current === type ? null : type));
  }, []);

  const registerDropdownRef = useCallback((type: DropdownType, ref: HTMLElement | null) => {
    if (ref) {
      dropdownRefs.current.set(type, ref);
    } else {
      dropdownRefs.current.delete(type);
    }
  }, []);

  // Handle outside click detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!activeDropdown) return;

      const target = event.target as Node;
      let isClickInside = false;

      // Check if click is inside any registered dropdown
      dropdownRefs.current.forEach((ref) => {
        if (ref && ref.contains(target)) {
          isClickInside = true;
        }
      });

      if (!isClickInside) {
        closeDropdown();
      }
    };

    // Handle escape key
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && activeDropdown) {
        closeDropdown();
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [activeDropdown, closeDropdown]);

  return {
    activeDropdown,
    isDropdownOpen,
    openDropdown,
    closeDropdown,
    toggleDropdown,
    registerDropdownRef,
  };
};
